import React, { useEffect, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthImage from "@/components/AuthImage";
import AttachmentViewer from "@/components/AttachmentViewer";
import MemberAvatar from "@/components/MemberAvatar";
import AuthVideo from "@/components/AuthVideo";
import { uploadVideoChunked, videoDuration, VIDEO_MAX, VIDEO_MAX_SECONDS } from "@/lib/video";
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Heart, MessageCircle, Repeat2, Megaphone, ImagePlus, Send, Loader2, X, Film, Globe2, Video, Play, Trash2, MoreHorizontal, Flag, Ban, Pencil } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ReportDialog, useBlock } from "@/components/Safety";
import { toast } from "sonner";

const timeAgo = (iso) => {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
};
const REGIONS = ["Nigeria", "UK", "USA"];

function Comments({ postId, onReport }) {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  useEffect(() => { api.get(`/posts/${postId}/comments`).then((r) => setList(r.data.comments)); }, [postId]);
  const add = async () => {
    if (!text.trim()) return;
    const { data } = await api.post(`/posts/${postId}/comments`, { content: text, parent_id: replyTo?.id || null });
    setList((l) => [...l, data.comment]); setText(""); setReplyTo(null);
  };
  const roots = list.filter((c) => !c.parent_id);
  const replies = (id) => list.filter((c) => c.parent_id === id);
  const Row = ({ c, depth }) => (
    <div className={`flex gap-2 ${depth ? "ml-8" : ""}`} data-testid={`comment-${c.id}`}>
      <Avatar className="h-7 w-7">
        <MemberAvatar name={c.author_name} photo={c.author_photo} className="h-7 w-7" />
      </Avatar>
      <div>
        <div className="rounded-2xl bg-secondary/70 px-3 py-1.5">
          <p className="text-xs font-700">{c.author_name}{c.author_branch && <span className="ml-1 rounded-full border px-1.5 text-[0.55rem] font-600 text-muted-foreground">{c.author_branch}</span>}</p>
          <p className="text-sm">{c.content}</p>
        </div>
        {!depth && <button onClick={() => setReplyTo(c)} className="ml-2 text-[0.65rem] text-[hsl(var(--water))]" data-testid={`reply-btn-${c.id}`}>Reply</button>}
        {c.author_id !== user?.id && <button onClick={() => onReport({ type: "comment", id: c.id })} className="ml-2 text-[0.65rem] text-muted-foreground hover:text-amber-500" data-testid={`report-comment-${c.id}`}>Report</button>}
      </div>
    </div>
  );
  return (
    <div className="mt-3 border-t border-[hsl(var(--gold))]/15 pt-3">
      <div className="space-y-2">
        {roots.map((c) => (
          <React.Fragment key={c.id}>
            <Row c={c} depth={0} />
            {replies(c.id).map((r) => <Row key={r.id} c={r} depth={1} />)}
          </React.Fragment>
        ))}
      </div>
      {replyTo && <p className="mt-2 text-xs text-muted-foreground">Replying to <b>{replyTo.author_name}</b> · <button onClick={() => setReplyTo(null)} className="text-[hsl(var(--water))]">cancel</button></p>}
      <div className="mt-2 flex gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder={replyTo ? "Write a reply…" : "Write a comment…"} className="h-9" data-testid={`comment-input-${postId}`} />
        <Button size="icon" className="h-9 w-9 shrink-0 bg-[hsl(var(--water))] text-white" onClick={add} data-testid={`comment-send-${postId}`}>
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function PostCard({ p, onLike, onReshare, onDelete, onReport, onBlock, onEdit }) {
  const { user, hasRole } = useAuth();
  const isGuest = user?.role === "guest";
  const mine = p.author_id === user?.id;
  const canDelete = !!user && (mine || hasRole("officer"));
  const canEdit = canDelete;
  const [open, setOpen] = useState(false);
  const [zoom, setZoom] = useState(null);
  return (
    <Card className={`glass p-5 ${p.is_announcement ? "border-[hsl(var(--gold))]/55" : "gold-ring"}`} data-testid={`post-${p.id}`}>
      <div className="flex items-center gap-3">
        <Avatar className="h-10 w-10">
          <MemberAvatar name={p.author_name} photo={p.author_photo} className="h-10 w-10" />
        </Avatar>
        <div className="flex-1">
          <p className="text-sm font-700">{p.author_name}{p.author_deck ? <span className="text-muted-foreground"> · "{p.author_deck}"</span> : null}</p>
          <p className="text-xs text-muted-foreground">{p.author_position}{p.author_frigate ? ` · ${p.author_frigate}` : ""} · {timeAgo(p.created_at)}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          {p.is_announcement && <Badge className="bg-[hsl(var(--gold))] text-[hsl(var(--primary-foreground))]"><Megaphone className="mr-1 h-3 w-3" />Official</Badge>}
          {p.region && <Badge variant="outline" className="border-[hsl(var(--water))]/40 text-[hsl(var(--water))]"><Globe2 className="mr-1 h-3 w-3" />{p.region}</Badge>}
        </div>
        {(canEdit || canDelete || !mine) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild><button className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground" aria-label="More" data-testid={`post-menu-${p.id}`}><MoreHorizontal className="h-4 w-4" /></button></DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="glass">
              {canEdit && <DropdownMenuItem onClick={() => onEdit(p)} data-testid={`edit-post-${p.id}`}><Pencil className="mr-2 h-4 w-4" />Edit {p.type === "reel" ? "reel" : "post"}</DropdownMenuItem>}
              {!mine && <DropdownMenuItem onClick={() => onReport({ type: "post", id: p.id })} data-testid={`report-post-${p.id}`}><Flag className="mr-2 h-4 w-4" />Report post</DropdownMenuItem>}
              {!mine && <DropdownMenuItem onClick={() => onBlock(p.author_id, p.author_name)} className="text-destructive focus:text-destructive" data-testid={`block-author-${p.id}`}><Ban className="mr-2 h-4 w-4" />Block {p.author_name?.split(" ")[0]}</DropdownMenuItem>}
              {canDelete && <DropdownMenuItem onClick={() => onDelete(p)} className="text-destructive focus:text-destructive" data-testid={`delete-post-${p.id}`}><Trash2 className="mr-2 h-4 w-4" />Delete {p.type === "reel" ? "reel" : "post"}</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {p.reshared_from && <p className="mt-2 text-xs text-muted-foreground"><Repeat2 className="mr-1 inline h-3 w-3" />Reshared from {p.reshared_from}</p>}
      <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed">{p.content}</p>
      {p.image_path && <button type="button" onClick={() => setZoom({ url: `/image/${p.image_path}`, content_type: "image/*", label: "Photo" })} className="mt-3 block w-full cursor-zoom-in overflow-hidden rounded-xl bg-black/5" data-testid={`post-image-${p.id}`}><AuthImage path={p.image_path} className="max-h-[32rem] w-full object-contain" /></button>}
      {p.video_path && <div className="mt-3 overflow-hidden rounded-xl bg-black"><AuthVideo path={p.video_path} className="max-h-[32rem] w-full" /></div>}
      <div className="mt-4 flex items-center gap-6 text-sm">
        <button onClick={() => onLike(p)} className={`flex items-center gap-1.5 transition-colors ${p.liked ? "text-rose-500" : "text-muted-foreground hover:text-rose-500"}`} data-testid={`like-button-${p.id}`}>
          <Heart className={`h-4 w-4 ${p.liked ? "fill-current" : ""}`} /> {p.likes_count}
        </button>
        <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-1.5 text-muted-foreground hover:text-[hsl(var(--water))]" data-testid={`comment-button-${p.id}`}>
          <MessageCircle className="h-4 w-4" /> {p.comments_count}
        </button>
        {!isGuest && <button onClick={() => onReshare(p)} className="flex items-center gap-1.5 text-muted-foreground hover:text-emerald-500" data-testid={`reshare-button-${p.id}`}>
          <Repeat2 className="h-4 w-4" /> {p.reshares}
        </button>}
      </div>
      {open && <Comments postId={p.id} onReport={onReport} />}
      {zoom && <AttachmentViewer items={[zoom]} title={zoom.label || "Photo"} onClose={() => setZoom(null)} />}
    </Card>
  );
}

export default function FeedView({ scope = "galleon", frigate = null, showRegions = false, showReel = true }) {
  const { hasRole, user } = useAuth();
  const isGuest = user?.role === "guest";
  const [posts, setPosts] = useState([]);
  const [reels, setReels] = useState([]);
  const [region, setRegion] = useState("All");
  const [content, setContent] = useState("");
  const [announce, setAnnounce] = useState(false);
  const [asReel, setAsReel] = useState(false);
  const [postRegion, setPostRegion] = useState("");
  const [imagePath, setImagePath] = useState(null);
  const [videoPath, setVideoPath] = useState(null);
  const [videoPct, setVideoPct] = useState(null);
  const [openReel, setOpenReel] = useState(null);
  const [report, setReport] = useState(null);
  const { block } = useBlock();
  const blockAuthor = async (id, name) => { if (await block(id, name)) { loadFeed(); loadReel(); } };
  const [posting, setPosting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const videoRef = useRef();

  const baseParams = scope === "frigate" ? { scope: "frigate", frigate } : { scope: "galleon" };

  const loadFeed = () => {
    const params = { ...baseParams, type: "post" };
    if (scope === "galleon" && region !== "All") params.region = region;
    api.get("/posts", { params }).then((r) => setPosts(r.data.posts));
  };
  const loadReel = () => {
    if (!showReel) return;
    api.get("/posts", { params: { ...baseParams, type: "reel" } }).then((r) => setReels(r.data.posts));
  };
  useEffect(() => { loadFeed(); loadReel(); /* eslint-disable-next-line */ }, [scope, frigate]);
  useEffect(() => { loadFeed(); /* eslint-disable-next-line */ }, [region]);

  const uploadImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/posts/image", fd); setImagePath(data.image_path); }
    catch { toast.error("Image upload failed"); } finally { setUploading(false); }
  };

  const uploadVideo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    e.target.value = "";
    if (f.size > VIDEO_MAX) { toast.error("Video must be 150 MB or smaller"); return; }
    const dur = await videoDuration(f);
    if (dur && dur > VIDEO_MAX_SECONDS) { toast.error("Clips must be 3 minutes or shorter"); return; }
    setVideoPct(0);
    try { const p = await uploadVideoChunked(f, setVideoPct); setVideoPath(p); setAsReel(true); toast.success("Video ready"); }
    catch (err) { toast.error(errMsg(err)); } finally { setVideoPct(null); }
  };

  const createPost = async () => {
    if (!content.trim() && !imagePath && !videoPath) return;
    if (asReel && !imagePath && !videoPath) { toast.error("Reels need a photo or video"); return; }
    setPosting(true);
    try {
      await api.post("/posts", {
        content, is_announcement: announce, image_path: imagePath, video_path: videoPath,
        frigate: scope === "frigate" ? frigate : null,
        region: scope === "galleon" && postRegion ? postRegion : null,
        type: asReel ? "reel" : "post",
      });
      setContent(""); setAnnounce(false); setAsReel(false); setImagePath(null); setVideoPath(null); setPostRegion("");
      loadFeed(); loadReel();
      toast.success(asReel ? "Added to the reel" : "Posted");
    } catch (e) { toast.error(errMsg(e)); } finally { setPosting(false); }
  };

  const like = async (p) => {
    const { data } = await api.post(`/posts/${p.id}/like`);
    setPosts((ps) => ps.map((x) => x.id === p.id ? { ...x, liked: data.liked, likes_count: data.likes_count } : x));
  };
  const reshare = async (p) => { await api.post(`/posts/${p.id}/reshare`); loadFeed(); toast.success("Reshared"); };
  const canDelete = (p) => !!user && (p.author_id === user.id || hasRole("officer"));
  const removePost = async (p) => {
    if (!window.confirm(p.type === "reel" ? "Delete this reel?" : "Delete this post?")) return;
    try { await api.delete(`/posts/${p.id}`); toast.success(p.type === "reel" ? "Reel deleted" : "Post deleted"); setOpenReel(null); loadFeed(); loadReel(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const [editing, setEditing] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [editImage, setEditImage] = useState(null);
  const [editUploading, setEditUploading] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const editFileRef = useRef();
  const startEdit = (p) => { setEditing(p); setEditContent(p.content || ""); setEditImage(p.image_path || null); setOpenReel(null); };
  const uploadEditImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return; e.target.value = "";
    setEditUploading(true);
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/posts/image", fd); setEditImage(data.image_path); }
    catch { toast.error("Image upload failed"); } finally { setEditUploading(false); }
  };
  const saveEdit = async () => {
    if (!editContent.trim() && !editImage && !editing.video_path) { toast.error("Post can't be empty"); return; }
    if (editing.type === "reel" && !editImage && !editing.video_path) { toast.error("A reel must keep a photo or video"); return; }
    setSavingEdit(true);
    try {
      await api.put(`/posts/${editing.id}`, { content: editContent, image_path: editImage, remove_image: !editImage });
      setEditing(null); loadFeed(); loadReel(); toast.success("Post updated");
    } catch (e) { toast.error(errMsg(e)); } finally { setSavingEdit(false); }
  };

  return (
    <div>
      {/* Reel strip */}
      {showReel && (
        <div className="mb-6">
          <div className="mb-2 flex items-center gap-2">
            <Film className="h-4 w-4 text-[hsl(var(--gold))]" />
            <h3 className="font-display text-lg font-700">Members' Reel</h3>
            <span className="text-xs text-muted-foreground">events & pictures from the brotherhood</span>
          </div>
          {reels.length === 0 ? (
            <Card className="gold-ring glass flex items-center justify-center p-6 text-sm text-muted-foreground">
              No reel posts yet — share an event photo or video using the composer below (tick “Reel”).
            </Card>
          ) : (
            <div className="flex gap-3 overflow-x-auto pb-2" data-testid="reel-strip">
              {reels.map((r) => (
                <div key={r.id} onClick={() => setOpenReel(r)} className="group relative h-48 w-36 shrink-0 cursor-pointer overflow-hidden rounded-2xl gold-ring bg-black/70" data-testid={`reel-${r.id}`}>
                  {r.video_path ? (
                    <>
                      <AuthVideo path={r.video_path} className="h-full w-full object-contain" controls={false} muted autoPlay loop />
                      <div className="pointer-events-none absolute inset-0 flex items-center justify-center"><div className="rounded-full bg-black/50 p-2"><Play className="h-5 w-5 text-white" /></div></div>
                    </>
                  ) : (
                    <AuthImage path={r.image_path} className="h-full w-full object-contain transition-transform group-hover:scale-105" />
                  )}
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                    <p className="line-clamp-2 text-[0.7rem] text-white">{r.content}</p>
                    <p className="mt-0.5 text-[0.6rem] text-white/70">{r.author_name}</p>
                  </div>
                  {canDelete(r) && <button onClick={(e) => { e.stopPropagation(); removePost(r); }} className="absolute right-1.5 top-1.5 rounded-full bg-black/55 p-1.5 text-white/90 backdrop-blur transition-colors hover:bg-destructive" title="Delete reel" aria-label="Delete reel" data-testid={`delete-reel-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Composer */}
      {!isGuest && <Card className="gold-ring glass mb-6 p-4" data-testid="post-composer">
        <Textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder={scope === "frigate" ? `Share with ${frigate} Frigate…` : "Share an update with the Galleon…"}
          className="min-h-[72px] resize-none border-0 bg-transparent focus-visible:ring-0" data-testid="post-content-input" />
        {imagePath && (
          <div className="relative mt-2 overflow-hidden rounded-xl bg-black/5">
            <AuthImage path={imagePath} className="max-h-60 w-full object-contain" />
            <Button size="icon" variant="secondary" className="absolute right-2 top-2 h-7 w-7" onClick={() => setImagePath(null)}><X className="h-4 w-4" /></Button>
          </div>
        )}
        {videoPath && (
          <div className="relative mt-2 overflow-hidden rounded-xl bg-black">
            <AuthVideo path={videoPath} className="max-h-60 w-full" />
            <Button size="icon" variant="secondary" className="absolute right-2 top-2 h-7 w-7" onClick={() => setVideoPath(null)} data-testid="remove-video-btn"><X className="h-4 w-4" /></Button>
          </div>
        )}
        {videoPct !== null && (
          <div className="mt-2" data-testid="video-upload-progress"><Progress value={videoPct} className="h-2" /><p className="mt-1 text-xs text-muted-foreground">Uploading video… {videoPct}%</p></div>
        )}
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-[hsl(var(--gold))]/15 pt-3">
          <div className="flex flex-wrap items-center gap-4">
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadImage} />
            <input ref={videoRef} type="file" accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm,.m4v" hidden onChange={uploadVideo} data-testid="post-video-input" />
            <Button variant="ghost" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="post-image-button">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4 text-[hsl(var(--water))]" />}
              <span className="ml-1 text-xs">Photo</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={() => videoRef.current?.click()} disabled={videoPct !== null} data-testid="post-video-button">
              {videoPct !== null ? <Loader2 className="h-4 w-4 animate-spin" /> : <Video className="h-4 w-4 text-[hsl(var(--water))]" />}
              <span className="ml-1 text-xs">Video</span>
            </Button>
            <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
              <Checkbox checked={asReel} onCheckedChange={setAsReel} data-testid="post-reel-checkbox" />
              <Film className="h-4 w-4 text-[hsl(var(--gold))]" /> Reel
            </label>
            {hasRole("officer") && (
              <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                <Checkbox checked={announce} onCheckedChange={setAnnounce} data-testid="post-announcement-checkbox" />
                <Megaphone className="h-4 w-4 text-[hsl(var(--gold))]" /> Announce
              </label>
            )}
          </div>
          <Button onClick={createPost} disabled={posting || (!content.trim() && !imagePath)} className="bg-[hsl(var(--gold))] font-700 text-[hsl(var(--primary-foreground))]" data-testid="social-post-create-button">
            {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Post"}
          </Button>
        </div>
      </Card>}

      {/* Region tabs */}
      {showRegions && (
        <div className="mb-4 flex flex-wrap gap-2" data-testid="region-tabs">
          {["All", ...REGIONS].map((r) => (
            <button key={r} onClick={() => setRegion(r)} data-testid={`region-${r}`}
              className={`rounded-full px-4 py-1.5 text-sm font-600 transition-colors ${region === r ? "bg-[hsl(var(--water))] text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
              {r === "All" ? "All Chapters" : r}
            </button>
          ))}
        </div>
      )}

      <div className="space-y-4">
        {posts.map((p) => <PostCard key={p.id} p={p} onLike={like} onReshare={reshare} onDelete={removePost} onReport={setReport} onBlock={blockAuthor} onEdit={startEdit} />)}
        <ReportDialog target={report} onClose={() => setReport(null)} />
        {posts.length === 0 && <Card className="gold-ring glass p-6 text-center text-sm text-muted-foreground">No posts here yet. Be the first to share.</Card>}
      </div>

      <Dialog open={!!openReel} onOpenChange={(o) => !o && setOpenReel(null)}>
        <DialogContent className="max-w-3xl bg-black p-0" data-testid="reel-player">
          <DialogHeader><DialogTitle className="sr-only">Reel</DialogTitle></DialogHeader>
          {openReel && (
            <div>
              {openReel.video_path
                ? <AuthVideo path={openReel.video_path} className="max-h-[80vh] w-full" autoPlay />
                : <AuthImage path={openReel.image_path} className="max-h-[80vh] w-full object-contain" />}
              <div className="flex items-start gap-3 p-4 text-white"><div className="flex-1"><p className="text-sm">{openReel.content}</p><p className="mt-1 text-xs text-white/60">{openReel.author_name}</p></div>
                {canDelete(openReel) && <Button size="sm" variant="ghost" className="text-white/80 hover:bg-white/10 hover:text-white" onClick={() => startEdit(openReel)} data-testid="reel-player-edit"><Pencil className="mr-1.5 h-4 w-4" />Edit</Button>}
                {canDelete(openReel) && <Button size="sm" variant="ghost" className="text-white/80 hover:bg-destructive hover:text-white" onClick={() => removePost(openReel)} data-testid="reel-player-delete"><Trash2 className="mr-1.5 h-4 w-4" />Delete</Button>}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="glass max-w-lg" data-testid="edit-post-dialog">
          <DialogHeader><DialogTitle className="font-display">Edit {editing?.type === "reel" ? "reel" : "post"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} className="min-h-[90px]" placeholder="Share an update…" data-testid="edit-post-content" />
              {editing.video_path ? (
                <div className="overflow-hidden rounded-xl bg-black"><AuthVideo path={editing.video_path} className="max-h-60 w-full" /></div>
              ) : editImage ? (
                <div className="relative overflow-hidden rounded-xl bg-black/5">
                  <AuthImage path={editImage} className="max-h-72 w-full object-contain" />
                  <Button size="icon" variant="secondary" className="absolute right-2 top-2 h-7 w-7" onClick={() => setEditImage(null)} data-testid="edit-remove-image"><X className="h-4 w-4" /></Button>
                </div>
              ) : null}
              <input ref={editFileRef} type="file" accept="image/*" hidden onChange={uploadEditImage} />
              <div className="flex items-center justify-between gap-2">
                {!editing.video_path
                  ? <Button variant="outline" className="gold-ring" onClick={() => editFileRef.current?.click()} disabled={editUploading} data-testid="edit-change-photo">{editUploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImagePlus className="mr-2 h-4 w-4" />}{editImage ? "Replace photo" : "Add photo"}</Button>
                  : <span />}
                <Button className="bg-[hsl(var(--gold))] font-700 text-[hsl(var(--primary-foreground))]" onClick={saveEdit} disabled={savingEdit} data-testid="edit-save-button">{savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
